import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '@/utils/api';

// ─── Utilitários ────────────────────────────────────────────────────────────

/** Verdadeiro se o utilizador está no iOS (iPhone/iPad) */
export function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !('MSStream' in window);
}

/** Verdadeiro se a app está em modo standalone (instalada como PWA) */
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

/** Converte base64url string para Uint8Array (applicationServerKey) */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
}

// ─── Subscrição interna ──────────────────────────────────────────────────────

async function doSubscribe() {
  const reg = await navigator.serviceWorker.ready;
  const { data } = await apiClient.get('/push/vapid-public-key');
  if (!data?.publicKey) throw new Error('Chave VAPID não disponível.');
  const applicationServerKey = urlBase64ToUint8Array(data.publicKey);
  const subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  const sub = subscription.toJSON();
  await apiClient.post('/push/subscribe', {
    endpoint: sub.endpoint,
    keys: { p256dh: sub.keys.p256dh, auth: sub.keys.auth },
  });
  localStorage.setItem('push-subscribed', '1');
}

// ─── Hook principal ──────────────────────────────────────────────────────────

/**
 * Gere subscrições push para Android/Chrome e iOS 16.4+ Safari PWA.
 *
 * Retorna:
 *   status  — 'idle' | 'requesting' | 'subscribed' | 'denied' | 'unsupported' | 'needs_install'
 *   requestPermission — função para pedir permissão manualmente (necessária no iOS e para botão explícito)
 */
export function usePushSubscription(isAuthenticated) {
  const [status, setStatus] = useState('idle');
  const attempted = useRef(false);

  const requestPermission = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setStatus('unsupported');
      return;
    }
    // iOS requer modo standalone para push
    if (isIOS() && !isStandalone()) {
      setStatus('needs_install');
      return;
    }
    try {
      setStatus('requesting');
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setStatus('denied'); return; }
      await doSubscribe();
      setStatus('subscribed');
    } catch {
      setStatus(Notification.permission === 'denied' ? 'denied' : 'idle');
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || attempted.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setStatus('unsupported');
      return;
    }
    if (Notification.permission === 'denied') { setStatus('denied'); return; }

    // iOS só consegue subscrever em standalone — não pedir automaticamente
    if (isIOS()) {
      setStatus(isStandalone() ? 'idle' : 'needs_install');
      return;
    }

    // Já subscrito neste browser
    if (localStorage.getItem('push-subscribed') === '1' && Notification.permission === 'granted') {
      setStatus('subscribed');
      return;
    }

    attempted.current = true;

    // Android/Chrome — pedir permissão automaticamente após 8s
    if (Notification.permission === 'default') {
      const timer = setTimeout(() => {
        Notification.requestPermission().then((perm) => {
          if (perm === 'granted') {
            doSubscribe()
              .then(() => setStatus('subscribed'))
              .catch(() => setStatus('idle'));
          } else {
            setStatus('denied');
          }
        });
      }, 8000);
      return () => clearTimeout(timer);
    }

    if (Notification.permission === 'granted') {
      doSubscribe()
        .then(() => setStatus('subscribed'))
        .catch(() => setStatus('idle'));
    }
  }, [isAuthenticated]);

  return { status, requestPermission };
}
